import { markStaleBundle } from './stale-bundle';

/**
 * Deploy skew detection, ahead of the first failure.
 *
 * Every build stamps one id into the bundle (`VITE_BUILD_ID`) and writes the
 * same id to `/version.json` next to it (see `vite.config.mts`). When the two
 * disagree, this tab is running code the server has replaced — mutations
 * against removed Convex functions are about to start failing, so we raise the
 * blocking dialog now rather than after a click is lost.
 */
const VERSION_URL = '/version.json';
const POLL_INTERVAL_MS = 5 * 60 * 1000;
/** Don't re-check on every tab switch. */
const FOCUS_THROTTLE_MS = 60 * 1000;

const BUILD_ID: string = import.meta.env.VITE_BUILD_ID ?? '';

/**
 * A missing or unreadable id proves nothing — only two known, different ids do.
 * Exported for the unit test.
 */
export function isStale(current: unknown, fetched: unknown): boolean {
  return (
    typeof current === 'string' &&
    typeof fetched === 'string' &&
    current !== '' &&
    fetched !== '' &&
    current !== fetched
  );
}

/**
 * The same question, asked once instead of on a timer: has the server moved to
 * another build? A failed chunk fetch needs this answer before it can blame a
 * deploy rather than the network (`lazy-page.ts`).
 *
 * Conservative like the poll — unreadable resolves to `false`, never `true`.
 * In dev there is no `version.json`, so this is always `false`.
 */
export async function isBundleStale(): Promise<boolean> {
  try {
    const response = await fetch(VERSION_URL, { cache: 'no-store' });
    if (!response.ok) return false;
    const { buildId } = (await response.json()) as { buildId?: unknown };
    return isStale(BUILD_ID, buildId);
  } catch {
    // Offline, proxied to an error page, served as HTML by a stray rewrite —
    // none of that is evidence the bundle is stale.
    return false;
  }
}

export function startVersionCheck() {
  // Dev serves no `version.json`, and a dev bundle is never behind a deploy.
  if (!import.meta.env.PROD || !BUILD_ID) return;

  let stopped = false;
  let lastCheck = 0;

  function stop() {
    stopped = true;
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
  }

  async function check() {
    if (stopped) return;
    lastCheck = Date.now();
    if (await isBundleStale()) {
      markStaleBundle();
      stop();
    }
  }

  function onVisible() {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - lastCheck < FOCUS_THROTTLE_MS) return;
    void check();
  }

  // Declared after `stop`, which closes over it — it only ever runs later.
  const timer = setInterval(() => void check(), POLL_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVisible);
}
